/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useContext, useState } from "react";

//MUI imports
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  Alert,
  Autocomplete,
  Avatar,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  Grow,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slide,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Apartment,
  ArrowCircleUp,
  BackspaceRounded,
  CheckCircle,
  East,
  MyLocation,
  SwapHoriz,
} from "@mui/icons-material";

import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
//Third-party libraries
import { DateTime } from "luxon";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

//Application specific imports
import { AuthenticatedContext } from "../Authenticated/Authenticated";
import {
  baseURL,
  checkDuplicateBookings,
  getAllLocations,
  searchPersonnel,
  singleBookingRequest,
} from "../../api";
import { PersonnelProfile } from "../PersonnelScreen/PersonnelScreen";
import { Location } from "SearchParameters";
import DynamicIcon from "../Icons/DynamicIcon";
import LocalizationProviderSetup from "../Helper/LocationProviderSetup";
import BookingCard from "./Helper/BookingCard";
import { BookingModel, BookingRequest } from "RosterData";
import BookingDate from "./Helper/BookingDate";
import BookingLocation from "./Helper/BookingLocation";
import BookingPersonnelSearch from "./Helper/BookingPersonnelSearch";
import BookingList from "./Helper/BookingList";

interface SingleBookingProps {
  close: any;
}

const SingleBooking = (props: SingleBookingProps) => {
  const { handleError, currentLocation, getLatestReportData } =
    useContext(AuthenticatedContext);
  const searchRef = React.useRef(null);

  const [startDate, setStartDate] = useState(DateTime.now());
  const [endDate, setEndDate] = useState(DateTime.now().plus({ week: 1 }));
  const [fromLocation, setFromLocation] = useState<Location | null>(null);
  const [toLocation, setToLocation] = useState<Location | null>(null);
  const [selectedPersonnel, setSelectedPersonnel] =
    useState<PersonnelProfile | null>(null);
  const [isReturn, setIsReturn] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const handleStartDateChange = (value: DateTime | null) => {
    if (value) {
      if (value > endDate) {
        setEndDate(value);
      }
      setStartDate(value);
    }
  };

  const handleEndDateChange = (value: DateTime | null) => {
    if (value) {
      setEndDate(value);
    }
  };

  const handleIsReturnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsReturn(event.target.checked);
  };

  const {
    data: duplicateBookings,
    isFetching: duplicateCheckLoading,
    refetch: checkDuplicates,
  } = useQuery(
    ["duplicateSearch"],
    () =>
      checkDuplicateBookings({
        startDate: startDate.toFormat("yyyy-LL-dd"),
        endDate: endDate.toFormat("yyyy-LL-dd"),
        fromLocation: fromLocation!.location,
        toLocation: toLocation!.location,
        personnelId: selectedPersonnel!.personnelId,
        isReturn: isReturn,
        replace: false,
      }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      initialData: [],
      onSuccess: (data: BookingModel[]) => {
        if (data.length === 0) {
          //Actually send booking create request
          createSingleBooking.mutate({
            startDate: startDate.toFormat("yyyy-LL-dd"),
            endDate: endDate.toFormat("yyyy-LL-dd"),
            toLocation: toLocation!.location,
            fromLocation: fromLocation!.location,
            isReturn: isReturn,
            replace: false,
            personnelId: selectedPersonnel!.personnelId,
          });
        }
      },
      onError: (error: AxiosError) => handleError(error),
    }
  );

  const createSingleBooking = useMutation({
    mutationFn: singleBookingRequest,
    onSuccess: () => {
      getLatestReportData();
      props.close();
    },
    onError: (err: AxiosError) => handleError(err),
  });

  const handleCreateBookingClicked = async () => {
    if (duplicateBookings.length > 0) {
      createSingleBooking.mutate({
        startDate: startDate.toFormat("yyyy-LL-dd"),
        endDate: endDate.toFormat("yyyy-LL-dd"),
        toLocation: toLocation!.location,
        fromLocation: fromLocation!.location,
        isReturn: isReturn,
        replace: true,
        personnelId: selectedPersonnel!.personnelId,
      });
    } else {
      await checkDuplicates();
    }
  };

  const handleClearButtonClicked = () => {
    setSelectedPersonnel(null);
    setIsReturn(false);
    setStartDate(DateTime.now());
    setEndDate(DateTime.now().plus({ week: 1 }));
    setFromLocation(null);
    setToLocation(null);
    queryClient.removeQueries(["duplicateSearch"]);
  };

  const handleFromLocationChange = (_event: any, newValue: Location | null) => {
    handleLocationChange(newValue, true);
  };

  const handleToLocationChange = (_event: any, newValue: Location | null) => {
    handleLocationChange(newValue, false);
  };

  const handleLocationChange = (newValue: Location | null, isFrom: boolean) => {
    const updateCurrentLocation = isFrom ? setFromLocation : setToLocation;
    const updateOppositeLocation = isFrom ? setToLocation : setFromLocation;
    const targetLocation = isFrom ? toLocation : fromLocation;

    //Update current location selection
    updateCurrentLocation(newValue);

    /*If the new value is not null (user changed/added value, not cleared the textfield)
     * proceed with changes to the opposite textfield
     * */
    if (newValue) {
      const isNewLocationSameAsCurrent =
        newValue.location === currentLocation!.location;
      if (isNewLocationSameAsCurrent) {
        //If the opposite location is the same as current Roster Location, then clear it
        if (targetLocation?.location === currentLocation!.location) {
          updateOppositeLocation(null);
        }
      } else {
        updateOppositeLocation(currentLocation);
      }
    }
  };

  const handleLocationSpecialAction = () => {
    const currentFromLocation = fromLocation;
    setFromLocation(toLocation);
    setToLocation(currentFromLocation);
  };

  const allParametersEntered = () => {
    return (
      !!startDate &&
      !!endDate &&
      !!fromLocation &&
      !!toLocation &&
      !!selectedPersonnel
    );
  };

  const locationContextComplete = (): boolean => {
    return !!startDate && !!endDate && !!fromLocation && !!toLocation;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox checked={isReturn} onChange={handleIsReturnChange} />
          }
          label="Return"
        />
        <BookingDate
          onStartDateChange={handleStartDateChange}
          startDate={startDate}
          onReturnDateChange={handleEndDateChange}
          returnDate={endDate}
          returnDateDisabled={!isReturn}
          returnMinDate={startDate}
        />
        <BookingLocation
          fromLocation={fromLocation}
          onFromLocationChange={handleFromLocationChange}
          toLocation={toLocation}
          onToLocationChange={handleToLocationChange}
          onError={handleError}
          specialButtonAction={handleLocationSpecialAction}
        />
        <div
          ref={searchRef}
          style={{
            width: "100%",
            paddingTop: 10,
          }}
        >
          {locationContextComplete() && (
            <BookingPersonnelSearch
              containerRef={searchRef.current}
              selectedPersonnel={selectedPersonnel}
              setSelectedPersonnel={setSelectedPersonnel}
              onError={handleError}
              isReturn={isReturn}
              fromDate={startDate}
              returnDate={endDate}
              destination={toLocation!}
            />
          )}
        </div>
        <div>
          {locationContextComplete() && duplicateBookings.length > 0 && (
            <BookingList dense={true}>
              {duplicateBookings.map((booking: BookingModel, index: number) => (
                <ListItem>
                  <ListItemIcon>
                    <ConfirmationNumberIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Person is already travelling on ${DateTime.fromISO(
                      booking.bookingDate
                    ).toFormat("dd/LL/yyyy")}`}
                    secondary={`Flight ${
                      booking.flight ? booking.flight.flightName : "WAITLIST"
                    } from ${booking.departureLocation} to ${
                      booking.destinationLocation
                    }`}
                  />
                </ListItem>
              ))}
            </BookingList>
          )}
        </div>
      </div>
      <div>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignSelf: "end",
            justifyContent: "space-between",
          }}
        >
          <Tooltip
            arrow
            placement="top"
            title={duplicateBookings.length > 0 ? "Overwrite" : "Book"}
          >
            <IconButton
              color={duplicateBookings.length > 0 ? "warning" : "success"}
              disabled={!allParametersEntered()}
              onClick={handleCreateBookingClicked}
            >
              {duplicateBookings.length > 0 ? (
                <PublishedWithChangesIcon />
              ) : (
                <CheckCircle />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip arrow title="Clear All" placement="top">
            <IconButton onClick={handleClearButtonClicked}>
              <BackspaceRounded />
            </IconButton>
          </Tooltip>
        </div>
        <div style={{ height: 4 }}>
          {(duplicateCheckLoading || createSingleBooking.isLoading) && (
            <LinearProgress />
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleBooking;
